import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Truck, MapPin, CalendarClock, Printer, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useApi, fileUrl, openBlob, errMsg } from "../lib/api";
import { PageHeader, DataState, AvailBadge, NativeSelect } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { VehicleForm } from "../components/Forms";
import { eur, fmtDate, todayISO, addDays } from "../lib/format";

export const VehicleThumb = ({ v, className = "h-36" }) => {
  const [err, setErr] = useState(false);
  const src = fileUrl(v.photo);
  return src && !err ? <img src={src} alt={v.label} onError={() => setErr(true)} className={`${className} w-full object-cover`} /> : (
    <div className={`${className} w-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900`}><Truck className="h-12 w-12 text-slate-600" /></div>
  );
};

const nextDeadline = (v) => {
  const list = [["Revisione", v.revision_date], ["Assicurazione", v.insurance_date], ["Bollo", v.tax_date]].filter((x) => x[1]).sort((a, b) => (a[1] < b[1] ? -1 : 1));
  return list[0];
};

const SOON_DAYS = 30;

function VehicleCard({ v, i, soon, onOpen }) {
  const nd = nextDeadline(v);
  return (
    <button onClick={() => onOpen(v.id)} className="surface overflow-hidden text-left hover:border-amber-500/60 transition-colors fade-up" style={{ animationDelay: `${i * 35}ms` }} data-testid={`vehicle-card-${v.id}`}>
      <div className="relative"><VehicleThumb v={v} /><div className="absolute top-2 left-2"><AvailBadge status={v.availability.status} testid={`vehicle-status-${v.id}`} /></div></div>
      <div className="p-4">
        <div className="eyebrow">{v.type}</div>
        <div className="font-semibold font-heading text-lg">{v.brand} {v.model}</div>
        <div className="text-xs font-mono text-muted-foreground">{v.plate}</div>
        <div className="text-xs mt-3 flex items-center gap-1 text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{v.availability.site_name || "In deposito"}</div>
        <div className="flex justify-between items-center mt-3 pt-3 border-t border-border/60 text-xs">
          {nd ? <span className={nd[1] <= soon ? "text-orange-300 flex items-center gap-1" : "text-muted-foreground flex items-center gap-1"}><CalendarClock className="h-3.5 w-3.5" />{nd[0]} {fmtDate(nd[1])}</span> : <span />}
          {v.daily_cost != null && <span className="num font-semibold">{eur(v.daily_cost)}/g</span>}
        </div>
      </div>
    </button>
  );
}

function VehiclesFilters({ q, setQ, type, setType, status, setStatus, types }) {
  return (
    <div className="surface p-3 mb-4 grid gap-2 sm:grid-cols-[1fr_200px_200px]">
      <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9 h-10" placeholder="Cerca marca, modello, targa…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="vehicles-search-input" /></div>
      <NativeSelect value={type} onChange={(e) => setType(e.target.value)} data-testid="vehicles-type-filter">
        <option value="">Tutte le tipologie</option>{types?.map((t) => <option key={t}>{t}</option>)}
      </NativeSelect>
      <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} data-testid="vehicles-status-filter">
        <option value="">Tutti gli stati</option><option value="disponibile">🟢 Disponibile</option><option value="in_cantiere">🔵 In Cantiere</option><option value="manutenzione">🟠 In Manutenzione</option>
      </NativeSelect>
    </div>
  );
}

function VehiclesActions({ query, onNew }) {
  const exportXlsx = () =>
    openBlob(`/vehicles/export?${query}`, "mezzi.xlsx")
      .then(() => toast.success("✓ Esportazione Excel completata"))
      .catch((e) => toast.error(errMsg(e)));
  return (
    <>
      <Button variant="outline" onClick={() => window.open(`/stampa-mezzi?${query}`, "_blank")} data-testid="vehicles-print-btn"><Printer className="h-4 w-4 mr-2" />Stampa elenco</Button>
      <Button variant="outline" onClick={exportXlsx} data-testid="vehicles-export-btn"><FileSpreadsheet className="h-4 w-4 mr-2" />Esporta Excel</Button>
      <Button onClick={onNew} data-testid="vehicles-new-btn"><Plus className="h-4 w-4 mr-2" />Nuovo mezzo</Button>
    </>
  );
}

export default function Vehicles() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const { data, loading, reload } = useApi("/vehicles", { q, type, status });
  const soon = addDays(todayISO(), SOON_DAYS);
  const query = new URLSearchParams({ q, type, status }).toString();
  const openVehicle = (id) => nav(`/mezzi/${id}`);
  return (
    <div>
      <PageHeader eyebrow="Anagrafica" title="Parco mezzi" subtitle="Mezzi, attrezzature, scadenze e disponibilità"
        actions={<VehiclesActions query={query} onNew={() => setOpen(true)} />} />
      <VehiclesFilters q={q} setQ={setQ} type={type} setType={setType} status={status} setStatus={setStatus} types={data?.types} />
      <DataState loading={loading && !data} empty={!data?.items?.length} emptyTitle="Nessun mezzo trovato">
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {(data?.items || []).map((v, i) => <VehicleCard key={v.id} v={v} i={i} soon={soon} onOpen={openVehicle} />)}
        </div>
      </DataState>
      <VehicleForm open={open} onOpenChange={setOpen} onSaved={reload} />
    </div>
  );
}
